import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  Button,
} from 'anf-core-react';
import FormHeading from './FormHeading';
import { FormCell, FormGroup, FormWrapper } from '../FormStructure';
import { TmntText } from '../Common';
import { ERROR_MESSAGE, LOADING_MESSAGE } from '../Messages/Messages';
import { finishPaneQuery } from './operations';
import { ModalContext } from '../../context/ModalContext/ModalContextProvider';
import { useCustomTealiumTrigger } from '../../hooks/useTealiumTrigger';

const PAGE_FINDING_METHOD = 'navigation - password reset modal';
const UNIVERSAL_CLICK_EVENT = 'universal_click';

export default function FinishPane({ hasProfileCompletionCreatePasswordFlowEnabled = false }) {
  const { loading, error: queryError, data: queryData } = useQuery(finishPaneQuery);
  const { closeModal } = useContext(ModalContext);

  // Analytics tracking setup
  const trackCompleteProfile = useCustomTealiumTrigger({
    eventName: UNIVERSAL_CLICK_EVENT,
    eventData: {
      event_name: 'my_account',
      page_finding_method: PAGE_FINDING_METHOD,
    },
  });

  const trackStartShopping = useCustomTealiumTrigger({
    eventName: UNIVERSAL_CLICK_EVENT,
    eventData: {
      event_name: 'homepage',
      page_finding_method: PAGE_FINDING_METHOD,
    },
  });

  if (loading) return LOADING_MESSAGE;
  if (queryError) return ERROR_MESSAGE;

  const { textFor = {} } = queryData;
  const {
    finishPaneHeader,
    finishPaneBodyText,
    finishPaneButton,
    finishPaneStoreSignupBodyText,
    finishPaneStoreSignupCompleteProfileButton,
    finishPaneStoreSignupStartShoppingButton,
  } = textFor;

  const getDescriptionText = () => (
    hasProfileCompletionCreatePasswordFlowEnabled
      ? finishPaneStoreSignupBodyText
      : finishPaneBodyText
  );

  const reloadPage = () => {
    localStorage.removeItem('loginSuccessMFE');
    window.location.reload();
  };

  const handleCompleteProfile = () => {
    trackCompleteProfile();

    closeModal();
    window.location.assign('/shop/UserAccountView');
  };

  const handleStartShopping = () => {
    trackStartShopping();

    closeModal();
    reloadPage();
  };

  const renderStoreSignupButtons = () => (
    <>
      <FormCell>
        <Button
          variant="tertiary-dark"
          isFullWidth
          onClick={handleCompleteProfile}
        >
          <TmntText tmnt={finishPaneStoreSignupCompleteProfileButton} />
        </Button>
      </FormCell>
      <FormCell>
        <Button
          variant="secondary"
          isFullWidth
          onClick={handleStartShopping}
        >
          <TmntText tmnt={finishPaneStoreSignupStartShoppingButton} />
        </Button>
      </FormCell>
    </>
  );

  const renderDefaultButton = () => (
    <FormCell>
      <Button
        variant="secondary"
        isFullWidth
        onClick={handleStartShopping}
      >
        <TmntText tmnt={finishPaneButton} />
      </Button>
    </FormCell>
  );

  const renderActionButtons = () => (
    hasProfileCompletionCreatePasswordFlowEnabled
      ? renderStoreSignupButtons()
      : renderDefaultButton()
  );

  return (
    <FormWrapper>
      <FormGroup>
        <FormCell>
          <FormHeading
            formHeadingText={finishPaneHeader}
            formDescriptionText={getDescriptionText()}
            formHeadingIcon="checkmark-circle-large-anf"
          />
        </FormCell>
        {renderActionButtons()}
      </FormGroup>
    </FormWrapper>
  );
}

FinishPane.propTypes = {
  hasProfileCompletionCreatePasswordFlowEnabled: PropTypes.bool.isRequired,
};
